import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Table, Card, Descriptions, Button, Tag, Space, Divider, Typography, message } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from '../../services/axios';

const { Title, Text } = Typography;

const ViewScheduleManagement = () => {
  const { fileReference } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [scheduleData, setScheduleData] = useState([]);
  const [fileInfo, setFileInfo] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { record } = location.state || {};

    if (!record) {
      fetchScheduleData(fileReference);
      return;
    }

    if (record.fileReference?.trim() !== fileReference?.trim()) {
      message.error('Invalid schedule data');
      navigate(-1);
      return;
    }

    setFileInfo({
      fileReference: record.fileReference,
      fileCategory: Array.isArray(record.category) ? record.category[0] : record.fileCategory,
      fileType: Array.isArray(record.type) ? record.type[0] : record.fileType,
      createdBy: record.createdBy || 'Unknown',
    });

    // Transform all scheduleManagement entries
    const transformedData = (record.scheduleManagement || []).map((item, index) => ({
      key: item._id || `${index}`,
      ...item,
      categoryDisplay: Array.isArray(item.category) ? item.category.join(', ') : item.category,
      typeDisplay: Array.isArray(item.type) ? item.type.join(', ') : item.type,
      projectName: item.projectName,
      discipline: item.discipline,
      description: item.description,
      reference: item.reference,
      plannedNumberOfDocuments: item.plannedNumberOfDocuments,
      plannedstartDate: item.plannedstartDate,
      plannedendDate: item.plannedendDate,
      plannedduration: item.plannedduration,
      earlystart: item.earlystart,
      earlyfinish: item.earlyfinish,
      latestart: item.latestart,
      latefinish: item.latefinish,
      totalfloat: item.totalfloat,
      freefloat: item.freefloat,
      remainingDuration: item.remainingDuration,
      revisionNo: item.revisionNo,
    }));

    setScheduleData(transformedData);
  }, [location.state, navigate, fileReference]);

  const fetchScheduleData = async (fileReference) => {
    setLoading(true);
    try {
      const res = await axios.post('/get-schedule-by-reference', {
        fileReference: fileReference?.trim(),
        user: localStorage.getItem('user'),
      });

      if (res && res.data && res.data.success && res.data.schedule) {
        const record = res.data.schedule;
        setFileInfo({
          fileReference: record.fileDetails?.[0]?.fileReference || fileReference,
          fileCategory: Array.isArray(record.fileDetails?.[0]?.fileCategory)
            ? record.fileDetails[0].fileCategory[0]
            : record.fileDetails?.[0]?.fileCategory,
          fileType: Array.isArray(record.fileDetails?.[0]?.fileType)
            ? record.fileDetails[0].fileType[0]
            : record.fileDetails?.[0]?.fileType,
          createdBy: record.createdBy || 'Unknown',
        });

        // Transform all scheduleManagement entries from API
        const transformedData = (record.scheduleManagement || []).map((item, index) => ({
          key: item._id || `${index}`,
          ...item,
          categoryDisplay: Array.isArray(item.category) ? item.category.join(', ') : item.category,
          typeDisplay: Array.isArray(item.type) ? item.type.join(', ') : item.type,
          projectName: item.projectName,
          discipline: item.discipline,
          description: item.description,
          reference: item.reference,
          plannedNumberOfDocuments: item.plannedNumberOfDocuments,
          plannedstartDate: item.plannedstartDate,
          plannedendDate: item.plannedendDate,
          plannedduration: item.plannedduration,
          earlystart: item.earlystart,
          earlyfinish: item.earlyfinish,
          latestart: item.latestart,
          latefinish: item.latefinish,
          totalfloat: item.totalfloat,
          freefloat: item.freefloat,
          remainingDuration: item.remainingDuration,
          revisionNo: item.revisionNo,
        }));

        setScheduleData(transformedData);
        if (transformedData.length === 0) {
          message.warning('No schedule management records found for this file reference');
        }
      } else {
        message.error('Schedule not found');
        navigate(-1);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      message.error(err.response?.data?.msg || 'Failed to fetch schedule data');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 60,
      fixed: 'left',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Project Name',
      dataIndex: 'projectName',
      key: 'projectName',
      width: 150,
      fixed: 'left',
      filterSearch: true,
      filters: [...new Set(scheduleData.map(item => item.projectName))].map(name => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.projectName === value,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (text, record) => (
        <Space direction="vertical" size={2}>
          {Array.isArray(record.category) ? (
            record.category.map((cat, idx) => (
              <Tag color="blue" key={idx}>{cat}</Tag>
            ))
          ) : (
            <Tag color="blue">{record.category}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (text, record) => (
        <Space direction="vertical" size={2}>
          {Array.isArray(record.type) ? (
            record.type.map((type, idx) => (
              <Tag color="green" key={idx}>{type}</Tag>
            ))
          ) : (
            <Tag color="green">{record.type}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Discipline',
      dataIndex: 'discipline',
      key: 'discipline',
      width: 120,
      filters: [...new Set(scheduleData.map(item => item.discipline))].map(disc => ({
        text: disc,
        value: disc,
      })),
      onFilter: (value, record) => record.discipline === value,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 120,
    },
    {
      title: 'Planned No. of Docs',
      dataIndex: 'plannedNumberOfDocuments',
      key: 'plannedNumberOfDocuments',
      width: 100,
      align: 'center',
    },
    {
      title: 'Planned Start Date',
      dataIndex: 'plannedstartDate',
      key: 'plannedstartDate',
      width: 130,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Planned End Date',
      dataIndex: 'plannedendDate',
      key: 'plannedendDate',
      width: 130,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Planned Duration',
      dataIndex: 'plannedduration',
      key: 'plannedduration',
      width: 100,
      align: 'center',
      render: (duration) => `${duration} days`,
    },
    {
      title: 'Early Start',
      dataIndex: 'earlystart',
      key: 'earlystart',
      width: 130,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Early Finish',
      dataIndex: 'earlyfinish',
      key: 'earlyfinish',
      width: 130,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Late Start',
      dataIndex: 'latestart',
      key: 'latestart',
      width: 130,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Late Finish',
      dataIndex: 'latefinish',
      key: 'latefinish',
      width: 130,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Total Float',
      dataIndex: 'totalfloat',
      key: 'totalfloat',
      width: 100,
      align: 'center',
      render: (float) => `${float} days`,
    },
    {
      title: 'Free Float',
      dataIndex: 'freefloat',
      key: 'freefloat',
      width: 100,
      align: 'center',
      render: (float) => `${float} days`,
    },
    {
      title: 'Remaining Duration',
      dataIndex: 'remainingDuration',
      key: 'remainingDuration',
      width: 120,
      align: 'center',
      render: (duration) => `${duration} days`,
    },
    {
      title: 'Revision No',
      dataIndex: 'revisionNo',
      key: 'revisionNo',
      width: 100,
      align: 'center',
      render: (rev) => <Tag color="purple">{rev}</Tag>,
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/home/create-schedule', { state: { selectedProject: location.state?.selectedProject } })}
              >
                Back
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                <FileTextOutlined /> Schedule Management Details
              </Title>
            </Space>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* File Information */}
          <Card size="small" title={<Text strong>File Information</Text>} style={{ background: '#fafafa' }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small">
              <Descriptions.Item label="File Reference">
                <Text strong>{fileInfo.fileReference}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="File Category">
                <Tag color="cyan">{fileInfo.fileCategory}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="File Type">
                <Tag color="green">{fileInfo.fileType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created By">
                <Text>{fileInfo.createdBy}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Schedule Management Table */}
          <Card
            title={
              <Space>
                <CalendarOutlined />
                <Text strong>Schedule Management Records</Text>
                <Tag color="blue">{scheduleData.length} Records</Tag>
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={scheduleData}
              loading={loading}
              scroll={{ x: 2500, y: 600 }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              bordered
              size="middle"
              sticky
            />
          </Card>
        </Space>
      </Card>
    </div>
  );
};

export default ViewScheduleManagement;